<template>
  <require-script
      v-if="apiKey"
      :src="`https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`"
  >
    <vue-google-autocomplete
        @hook:mounted="initGoogleAutocomplete"
        ref="full_address"
        id="map"
        :class="`place-google-autocomplete w-full border-solid border text-inherit ${inputClasses || ''}`"
        placeholder="Please type your address"
        @placechanged="getAddressData"
        name="full_address"
        types="address"
        country="us"
        @blur="checkInput"
        @no-results-found="() => this.$emit('no_results_found')"
    >
    </vue-google-autocomplete>
  </require-script>
  <span v-else>Missing configuration for Google Place API</span>
</template>
<script>

import VueGoogleAutocomplete from "vue-google-autocomplete";
import RequireScript from "./RequireScript.vue";
import Configuration from "../../mixins/Configuration";

/**
 * Requires Vue Google Autocomplete. Add to main project using:
 *
 * npm install vue-google-autocomplete --save
 */
export default {
  components: {RequireScript, VueGoogleAutocomplete},
  props: {
    inputClasses: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
  },
  data: function () {
    return {
      full_address: '',
      address_data: {},
    };
  },
  computed: {
    apiKey() {
      return this.getConfiguration('google.places_api_key')
    },
  },
  mixins: [Configuration],

  methods: {
    initGoogleAutocomplete() {
      if (this.value) {
        this.$refs.full_address.$el.value = this.value;
      }
    },

    /**
     * When the location found
     * @param {Object} addressData Data of the found location
     * @param {Object} placeResultData PlaceResult object
     * @param {String} id Input container ID
     */
    getAddressData: function (addressData, placeResultData) {
      // Get the full address string
      const fullAddressStr = this.$refs.full_address.$el.value;

      // Extract missing fields from full address if needed
      if (fullAddressStr && (!addressData.administrative_area_level_1 || !addressData.locality)) {
        const addressParts = fullAddressStr.split(',').map(part => part.trim());

        // Find state (administrative_area_level_1)
        let stateIndex = -1;
        if (!addressData.administrative_area_level_1) {
          // First look for exact 2-letter state code
          for (let i = 0; i < addressParts.length; i++) {
            const stateMatch = addressParts[i].match(/^([A-Z]{2})$/);
            if (stateMatch) {
              stateIndex = i;
              addressData.administrative_area_level_1 = stateMatch[1];
              break;
            }
          }

          // If not found, look for state code within text
          if (stateIndex === -1) {
            for (let i = 0; i < addressParts.length; i++) {
              const stateMatch = addressParts[i].match(/\b([A-Z]{2})\b/);
              if (stateMatch) {
                stateIndex = i;
                addressData.administrative_area_level_1 = stateMatch[1];
                break;
              }
            }
          }
        }

        // Find city (locality)
        if (!addressData.locality) {
          // Try to find the state in address parts (either from API or extracted)
          const stateValue = addressData.administrative_area_level_1;
          let foundStateIndex = -1;
          if (stateValue) {
            // Look for the state in the address parts
            for (let i = 0; i < addressParts.length; i++) {
              if (addressParts[i].includes(stateValue)) {
                foundStateIndex = i;
                break;
              }
            }
          }

          // If state was found and not the first part, use part before it as city
          if (foundStateIndex > 0) {
            addressData.locality = addressParts[foundStateIndex - 1];
          }
          // Otherwise use second part if available (common city position)
          else if (addressParts.length > 1) {
            addressData.locality = addressParts[1];
          }
          // Last resort: use first part if that's all we have
          else if (addressParts.length > 0) {
            addressData.locality = addressParts[0];
          }
        }
      }

      // Create full_address object with enhanced data
      this.full_address = {
        ...addressData,
        name: placeResultData.name,
        full: fullAddressStr
      };

      this.$refs.full_address.$el.value = this.full_address?.name ?? '';

      this.$emit('full_address', this.full_address);
    },
    checkInput: function () {
      if (!this.$refs.full_address.$el.value.trim()) {
        this.full_address = '';
        this.$emit('full_address', this.full_address);
      }
    },
  },
};
</script>

<style>
.place-google-autocomplete {
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  color: #202028 !important;
}
</style>
